import React, { useEffect, useState } from 'react';

import { ChevronDown, Sun, Moon, Tag } from 'lucide-react';

import ProfileMenu from './ProfileMenu';
import packageJson from '../../../package.json'; // Adjust the path to your `package.json`
import { API_BASE_URL } from '../../config/config';
import { UserClient } from '../../services/apiClient';
import axiosInstance from '../../services/axiosInstance';
import {
  getUserName,
  getAdminUserName,
  getUserId,
} from '../../utils/tokenUtils';

const Topbar: React.FC = () => {
  const userId = getUserId();
  const userName = getUserName() || '';
  const adminUsername = getAdminUserName();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false); // For managing the toggle state
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(
    () => {
      // Get stored URL immediately on mount
      const storedData = localStorage.getItem(`profilePic_${userId}`);
      if (storedData) {
        const { url, expiresAt } = JSON.parse(storedData);

        return expiresAt > Date.now() ? url : null; // Only use if not expired
      }

      return null;
    },
  );
  const userClient = new UserClient(API_BASE_URL, axiosInstance);

  useEffect(() => {
    // Load dark mode state from localStorage on mount
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleProfileMenu = (): void => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const toggleDarkMode = (): void => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode)); // Save to localStorage
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Function to get stored SAS URL from localStorage
  const getStoredSasUrl = (): {
    url: string | null;
    expiresAt: number | null;
  } => {
    const storedData = localStorage.getItem(`profilePic_${userId}`);
    if (!storedData) {
      return { url: null, expiresAt: null };
    }

    const { url, expiresAt } = JSON.parse(storedData);

    return { url, expiresAt };
  };

  // Function to save SAS URL in localStorage
  const storeSasUrl = (url: string, expiresInMinutes: number): void => {
    const expiresAt = Date.now() + (expiresInMinutes - 1) * 60 * 1000; // Expire 1 min before the actual expiry
    localStorage.setItem(
      `profilePic_${userId}`,
      JSON.stringify({ url, expiresAt }),
    );
  };

  // Fetch profile picture SAS URL
  useEffect(() => {
    if (!userId) {
      return;
    }

    const { url: storedUrl, expiresAt } = getStoredSasUrl();

    // If stored SAS URL is valid, use it immediately
    if (storedUrl && expiresAt && expiresAt > Date.now()) {
      setProfilePictureUrl(storedUrl);
    }

    const fetchProfilePicture = async (): Promise<void> => {
      try {
        const profilePicResponse =
          await userClient.generateProfilePictureSas(userId);
        if (profilePicResponse.success) {
          const newUrl = profilePicResponse.data ?? '';
          setProfilePictureUrl(newUrl);
          storeSasUrl(newUrl, 5);
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    // Only fetch a new SAS token if it's expired or about to expire
    if (!storedUrl || !expiresAt || expiresAt < Date.now()) {
      fetchProfilePicture();
    }
  }, [userId]);

  const getInitials = (name: string): string => {
    const initials = name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();

    return initials || 'U';
  };

  return (
    <header className="flex items-center justify-between h-20 px-4 lg:px-6 bg-white dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700">
      <div className="flex items-center space-x-4">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img src="/images/HuntersPortalLogo.png" alt="Logo" className="h-12 w-auto" />
        </a>

        {/* Version */}
        <div className="hidden lg:flex items-center ml-4 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 shadow-sm">
          <Tag className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            v.{packageJson.version}
          </span>
        </div>
      </div>

      {/* Notifications, Messages, Dark Mode Toggle, and Profile */}
      <div className="flex items-center space-x-4">
        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          aria-label="Toggle Dark Mode"
        >
          {isDarkMode ? (
            <Sun className="w-5 h-5" aria-hidden="true" />
          ) : (
            <Moon className="w-5 h-5" aria-hidden="true" />
          )}
        </button>

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={toggleProfileMenu}
          >
            {/* Profile Picture or Initials */}
            {profilePictureUrl ? (
              <img
                className="w-10 h-10 rounded-full"
                src={profilePictureUrl}
                alt="Profile"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary dark:bg-gray-700 text-white flex items-center justify-center text-sm font-semibold">
                {getInitials(userName)}
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-gray-600 dark:text-gray-300 hidden sm:block">
                {userName}
              </span>
              {adminUsername && (
                <span className="text-gray-500 dark:text-gray-400 text-xs hidden sm:block">
                  {adminUsername}
                </span>
              )}
            </div>
            <ChevronDown
              className={`w-6 h-6 text-gray-600 dark:text-gray-300 transition-transform ${profileMenuOpen ? 'rotate-180' : ''
                }`}
            />
          </button>

          {profileMenuOpen && <ProfileMenu />}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
